import { Injectable } from '@angular/core';
import { Application, ApplicationHDNRequest, ApplicationHDNResponse, CordHdnService, CordMidOfficeService, HdnRecord, HdnRecordHeader, HdnRecordHeaderRequestSchema, HdnRecordMiscellaneous, HdnRecordPatch, HdnRecordSendingApplication, NewMessage } from '@yellowtail-frontend/cord-connector';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { HdnMessageLookupModel, KeyValueModel } from '../../models/keyValue';
import { ValidationResult } from '../../models/formly.model';
import { ValidationErrors } from '@angular/forms';
import { VariableService } from '../utilities/variables.service';
import moment from 'moment';
import { NationalityLookupModel } from '../../models/nationalityLookup.model';
import { HelperService } from '../utilities/helper.service';
import { ApplicationHeaderData, OrganizationExtended, PersonExtended } from 'src/app/models/component.model';
import {
    LegalPersonType,
    ManualHDNLoanType,
    OrganizationType,
    StorageObjects,
    UserType
} from 'src/app/utilities/enum';
import {
    ApplicationStatusMessageStatus,
    ApplicationStatusMessageText,
    ApplicationStatusMessageType
} from 'src/app/utilities/application-status.enum';
import { ApplicationService } from './application.service';
import { StorageService } from '@yellowtail-frontend/storage-service';
import { environment } from 'src/environments/environment';
import { OfficeService } from './office.service';
import { cloneDeep } from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class AxService {

    alerts$ = new BehaviorSubject<Array<ValidationResult>>([]);
    requiredFieldsObservable = new BehaviorSubject<string>('');
    creditProviderUpdatedObservarable = new Subject<string>();
    reactivateCreditProviderObservalble = new Subject<string>();
    deactivateCreditProviderObservable = new Subject<string>();
    reactivateCreditProviderObservable = new Subject<string>();

    deleteFormlyItem = new Subject<string>();


    creditProviderUpdatedSubscription: Subscription;

    creditProviderSelectControl: any; // TODO: Add type
    originalCreditProvider: string = '';

    cordRefLayout: string[] = [];

    constructor(private readonly _cordMidOfficeService: CordMidOfficeService,
        private readonly _cordHdnService: CordHdnService,
        private readonly _helperService: HelperService,
        private readonly _variableService: VariableService,
        private readonly _applicationService: ApplicationService,
        private readonly _storageService: StorageService,
        private readonly _officeService: OfficeService
    ) {
    }

    getAXDataSchema(applicationId: string): Promise<unknown> {
        return this._cordMidOfficeService.getAXDataSchema(applicationId);
    }

    getAXData(applicationId: string): Promise<any> {
        const promise = new Promise<unknown>((resolve, reject) => {
            this._cordMidOfficeService.getAXData(applicationId)
                .then((response: any) => {
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response.status === 404) {
                        resolve({});
                    } else {
                        reject(error);
                    }
                });
        });

        return promise;
    }

    saveAxData<T>(applicationId: string, data: any): Promise<T> {
        return this._cordMidOfficeService.saveAxData<T>(applicationId, data);
    }

    sendAxData(applicationId: string, data: NewMessage): Promise<string> {
        const USER_TYPE = this._storageService.get<string>(StorageObjects.userType);
        return new Promise((resolve, reject) => {
            if (USER_TYPE === UserType[UserType.Agent] || USER_TYPE === UserType[UserType.SuperAgent]) {
                this._cordHdnService.createHdnDossier(applicationId)
                    .then((hdnDossierId: string) => {
                        resolve(hdnDossierId);
                    })
                    .catch(err => {
                        reject(err);
                    })
            } else {
                this._cordHdnService.sendAxMessage(applicationId, data)
                    .then(() => {
                        resolve('');
                    })
                    .catch(err => {
                        reject(err);
                    })
            }
        })
        
    }

    getHdnRecordData(applicationId: string): Promise<HdnRecord> {
        return this._cordHdnService.getHdnRecord(applicationId);
    }

    getAxLookupData(parent: string = 'overview'): Promise<KeyValueModel[]> {

        const promise = new Promise<KeyValueModel[]>((resolve, reject) => {
            let schema;
            fetch('/assets/json/axLookup.json').then((response) => {
                schema = response.json();
                resolve(schema);
            });
        });

        return promise;
    }

    getAxLookupHDNMessageData(parent: string = 'overview'): Promise<HdnMessageLookupModel[]> {

        const promise = new Promise<HdnMessageLookupModel[]>((resolve, reject) => {
            let schema;
            fetch('/assets/json/axLookupHDNmessages.json')
                .then((response) => {
                    schema = response.json();
                    resolve(schema);
                });
        });

        return promise;
    }

    getNationalityLookupData(parent: string = 'overview'): Promise<NationalityLookupModel[]> {

        const promise = new Promise<NationalityLookupModel[]>((resolve, reject) => {
            this.readNationalityLookupFile().then(response => {
                const NATIONS = [];
                let _nationality = {};
                for (const nation of response.split(/[\r\n]+/)) {
                    if (nation.split('.')[0] !== 'xx') {
                        _nationality = {
                            nationality: nation.split('.')[2].trim().split(' ')[1],
                            code: nation.split('.')[2].trim().split(' ')[0],
                            active: true
                        };
                    } else {
                        _nationality = {
                            nationality: nation.split('.')[2].trim().split(' ')[1],
                            code: nation.split('.')[2].trim().split(' ')[0],
                            active: false
                        };
                    }

                    NATIONS.push(_nationality);
                }

                resolve(NATIONS);
            });
        });

        return promise;
    }

    saveAndSendAXData(applicationId: string, model: any): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this._helperService.createApplicationStatus(
                ApplicationStatusMessageType.ManualApplication,
                ApplicationStatusMessageStatus.HandmatigeOfferteaanvraagOntvangenDoorMO,
                ApplicationStatusMessageText.NieuweAanvraag,
                applicationId,
            )
                .then(() => {
                    const UPDATED_MODEL = cloneDeep(model);
                    delete UPDATED_MODEL.Header;
                    this.saveAxData(applicationId, this.appendCordRefId(UPDATED_MODEL))
                        .then(() => {
                            const DATA_MESSAGE: NewMessage = {
                                Id: this._helperService.newGuid(),
                                MessageRecipient: OrganizationType.CreditProvider,
                                MessageType: 'AX'
                            };
                            this.sendAxData(applicationId, DATA_MESSAGE)
                                .then((hdnDossierId: string) => {
                                    resolve(hdnDossierId);
                                })
                                .catch((sendError) => {
                                    reject(sendError);
                                });
                        })
                        .catch((sendError) => {
                            reject(sendError);
                        });
                })
                .catch((sendError) => {
                    reject(sendError);
                });
        });
    }

    saveCordFinanceRelatedData(application: Application, model: any, includeHdnData?: boolean): Promise<[Application, ApplicationHDNResponse]> {
        const REQUEST: ApplicationHDNRequest = {
            ReceiverCode: model.Header.OntvangerCode,
            HDNReferenceNumber: model.Header.AanvraagVolgNr,
            Types: model.Lening?.Regeling ? [ManualHDNLoanType[model.Lening?.Regeling]] : ['NewMortgage'],
            HDNVersion: model.Header.AanvraagVersie,
            IsActive: true
        }

        return Promise.all([
            this._applicationService.updateApplication(application),
            includeHdnData ? this._applicationService.updateApplicationHDNData(REQUEST, application.ApplicationID) : undefined
        ]);
    }

    appendCordRefId(obj): any[] {
        const result = [];
        for (const prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                const value = obj[prop];
                if (typeof value === 'object') {
                    // check each property
                    let _allowCordRefProperty = false;
                    for (const internalProperty in value) {
                        if (value.hasOwnProperty(internalProperty)) {
                            const PROPERTY_VALUE = value[internalProperty];
                            if (typeof value === 'object') {
                                _allowCordRefProperty = true;
                            } else {
                                if (PROPERTY_VALUE.length > 0) {
                                    _allowCordRefProperty = true;
                                }
                            }
                        }
                    }

                    obj['Cord_Ref'] = this._helperService.newGuid();

                    if (obj['Volgnummer']?.toString().includes('.')) {
                        obj['Volgnummer'] = parseInt(obj['Volgnummer'].toString(), 0);
                    }

                    result.push(this.appendCordRefId(value));
                } else {
                    obj['Cord_Ref'] = this._helperService.newGuid();

                    if (obj['OverbruggingJN']?.toLowerCase().includes('ja')) {
                        if (obj['Overbrugging'] === undefined) {
                            obj['Overbrugging'] = {
                                Cord_Ref: this._helperService.newGuid(),
                            };
                        }
                    }

                    if (obj['Volgnummer']?.toString().includes('.')) {
                        obj['Volgnummer'] = parseInt(obj['Volgnummer'].toString(), 0);
                    }
                }
            }
        }
        return obj;
    }

    addTitlesToSchema(object: any, depth = 0, title?: string): any {
        if (title && !object.title) {
            object.title = title;
        }

        if (object.type === 'object' && object.properties) {
            if (depth === 2) {
                object.description = JSON.stringify({ expandable: true });
            }

            for (const key of Object.keys(object.properties)) {
                object.properties[key] = this.addTitlesToSchema(object.properties[key], depth + 1, key);
            }
        }

        if (object.type === 'array') {
            // Only ever pick first item from array and convert it to object.
            if (Array.isArray(object.items)) {
                object.items = this.addTitlesToSchema(object.items[0], depth + 1, 'Item');
            } else {
                object.items = this.addTitlesToSchema(object.items, depth + 1, 'Item');
            }

            if (depth === 2) {
                object.description = JSON.stringify({ expandable: true });
            }
        } else if (object.type === undefined) {

        }

        return object;
    }

    getTopLevelErrorMessageForAXValidation(): string {
        return 'De aanvraag kan niet worden beoordeeld. De gegevens konden niet worden opgehaald. Om de aanvraag zelf te controleren kunt u naar de Hypobieb gaan. Deze vindt u rechtsboven aan op het Dashboard scherm.';
    }

    buildApplicationObjectForAXSave(application: Application, model: any, nationalityList: NationalityLookupModel[], clientId: string, partnerId?: string[]): Application {
        const DEFAULT_DATA = this._helperService.defaultApplicationData();
        application.Person = application.Person === undefined ? [] : application.Person;

        if (model?.PartijNAWData?.length > 0) {
            application.Person = [];
            let naturalPersonIndex = 0;

            model.PartijNAWData.forEach((_person) => {
                if (
                    (_person.SoortPartij === undefined || _person.SoortPartij.toString().toLowerCase() === '01 natuurlijk persoon')
                    &&
                    !_person.isDeleted
                ) {
                    let personDateOfBirth = '';
                    let personYear = '';
                    let personMonth = '';
                    let personDay = '';
                    let personNationality = '';

                    if (_person.GeboorteDt?.length > 0) {
                        const BIRTH_DATE = moment(_person.GeboorteDt);
                        personYear = BIRTH_DATE ? BIRTH_DATE.year().toString() : '1970';
                        personMonth = BIRTH_DATE ? (BIRTH_DATE.month() + 1).toString().padStart(2, '0') : '01';
                        personDay = BIRTH_DATE ? BIRTH_DATE.date().toString() : '01';

                        personDateOfBirth = personYear + '-' + personMonth + '-' + personDay;
                    }

                    if (_person.Nationaliteit) {
                        const NATIONALITY_VALID: NationalityLookupModel = nationalityList.find(x => x.code === _person.Nationaliteit.toString().split(' ')[0]);
                        if (NATIONALITY_VALID.active === true) {
                            personNationality = NATIONALITY_VALID.code;
                        } else {
                            personNationality = 'Unknown';
                        }
                    }

                    const PERSON_OBJECT: PersonExtended = {
                        ID: naturalPersonIndex === 0 ? clientId : partnerId?.length === 0 ? this._helperService.newGuid() : partnerId[naturalPersonIndex - 1],
                        ApplicationID: application.ApplicationID,
                        Type: naturalPersonIndex === 0 ? LegalPersonType.Client : LegalPersonType.Partner,
                        FirstName: _person.VoorNamen || DEFAULT_DATA.FIRST_NAME,
                        Surname: _person.GeboorteAchternaam || DEFAULT_DATA.LAST_NAME,
                        Initials: _person.VoorLetters || DEFAULT_DATA.INITIALS,
                        Prefix: _person.GeboorteTussenvoegsels || DEFAULT_DATA.PREFIX,
                        DateOfBirth: personDateOfBirth || DEFAULT_DATA.DATE_OF_BIRTH,
                        Nationality: _person.Nationaliteit ? personNationality : DEFAULT_DATA.NATIONALITY,
                        Addresses: [{ PostalCode: _person.Postcode || DEFAULT_DATA.POSTAL_CODE }]
                    };

                    if (naturalPersonIndex > 1) { // This means three natural persons have been added
                        // Set all persons after the second person to type is Unknown, CORD cannot handle more than
                        // 1 client or partner type.
                        PERSON_OBJECT.Type = LegalPersonType.Unknown;
                    }
                    

                    const PERSON_INDEX = application.Person.findIndex(x => x.ID === (naturalPersonIndex === 0 ? clientId : partnerId[naturalPersonIndex - 1]));
                    if (PERSON_INDEX > -1) {
                        application.Person[PERSON_INDEX] = PERSON_OBJECT;
                    } else {
                        application.Person.push(PERSON_OBJECT);
                    }

                    naturalPersonIndex++;
                }
            });
        } else {
            if (!application.Person.length) {
                application.Person.push({
                    ID: clientId,
                    ApplicationID: application.ApplicationID,
                    Type: LegalPersonType.Client,
                    FirstName: DEFAULT_DATA.FIRST_NAME,
                    Surname: DEFAULT_DATA.LAST_NAME,
                    Initials: DEFAULT_DATA.INITIALS,
                    DateOfBirth: DEFAULT_DATA.DATE_OF_BIRTH,
                    Nationality: DEFAULT_DATA.NATIONALITY,
                    Addresses: [{ PostalCode: DEFAULT_DATA.POSTAL_CODE }]
                });
            }
        }

        if (!application.Person.length) {
            application.Person.push({
                ID: clientId,
                ApplicationID: application.ApplicationID,
                Type: LegalPersonType.Client,
                FirstName: DEFAULT_DATA.FIRST_NAME,
                Surname: DEFAULT_DATA.LAST_NAME,
                Initials: DEFAULT_DATA.INITIALS,
                DateOfBirth: DEFAULT_DATA.DATE_OF_BIRTH,
                Nationality: DEFAULT_DATA.NATIONALITY,
                Addresses: [{ PostalCode: DEFAULT_DATA.POSTAL_CODE }]
            });
        }

        return application;
    }

    async postHdnRecordData(applicationId: string, type: 'post' | 'patch', creditProvider: OrganizationExtended, hdnDossierId?: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            const HDN_CONFIGURATION_DATA = await this._applicationService.getHdnCOnfigurationData()
            const ADVISOR_ORGANIZATION = await this._officeService.getOfficeDetails(this._storageService.get<string>(StorageObjects.userOrganizationId));
    
            const SENDING_APPLICATION: HdnRecordSendingApplication = {
                ApplicationName: "Hypact Hub",
                ApplicationVersion: HDN_CONFIGURATION_DATA.ApplicationVersion,
                SendingDateTime: moment.utc().format(this._helperService.getDateFormat(false, true))
            };
    
            const MISCELLANEOUS: HdnRecordMiscellaneous = {
                SenderName: ADVISOR_ORGANIZATION.Name,
                ReceiverName: creditProvider.ShortName,
                SendingApplication: SENDING_APPLICATION
            }
    
            const REQUEST_SCHEMA: HdnRecordHeaderRequestSchema = {
                MessageType: "AX OfferteAanvraag",
                ReceiverCode: creditProvider.ShortName,
                SchemaVersion: HDN_CONFIGURATION_DATA.SchemaVersion.toString(),
                ContentType: "XML",
                Environment: environment.environment.toLowerCase()
            }
    
            const RECORD_HEADER: HdnRecordHeader = {
                Sender: HDN_CONFIGURATION_DATA.SenderId,
                Receiver: creditProvider.HdnNumber,
                RequestVersion: "1",
                RequestTraceNr: this._helperService.newGuid(),
                RequestSchema: REQUEST_SCHEMA
            }
    
            const HDN_DATA: HdnRecord = {
                HdnDossierId: hdnDossierId ? hdnDossierId : applicationId,
                ApplicationId: applicationId,
                RecordHeader: RECORD_HEADER,
                Miscellaneous: MISCELLANEOUS
            }
    
            var hdnRecordData = HDN_DATA;
            if (type === 'post') {
                resolve(this._applicationService.postHdnRecordData(HDN_DATA));
            } else {
                const PATCH_DATA: HdnRecordPatch[] = [
                    {
                        op: 'replace',
                        path: '/RecordHeader/receiver',
                        value: creditProvider.ShortName
                    },
                    {
                        op: 'replace',
                        path: '/RecordHeader/requestSchema/receiverCode',
                        value: creditProvider.ShortName
                    },
                    {
                        op: 'replace',
                        path: '/HdnDossierId',
                        value: hdnDossierId ? hdnDossierId : hdnRecordData.HdnDossierId
                    }
                ]
    
                resolve(this._applicationService.patchHdnRecordData(PATCH_DATA, applicationId));
            }  
        });      
    }

    private readNationalityLookupFile(): Promise<string> {
        const promise = new Promise<string>((resolve, reject) => {
            fetch('/assets/json/nationalityLookup.txt').then((response) => {
                resolve(response.text());
            });
        });

        return promise;
    }

    // TODO: Add type | generic
    async getAXDefaultApplicationData(): Promise<any> {
        const schema = await (await fetch('/assets/json/defaultApplicationData.json')).json();
        const GUID  = this._helperService.newIdRef();
        schema.TussenPersoon.RefContactPersoonData.IDREF = GUID;
        schema.PartijNAWData[0].ID = GUID;
        return schema;
    }

    validateAxData(data, applicationId, otherData): Promise<void | { result: boolean, data: ValidationResult[] }> {
        return new Promise<void | { result: boolean, data: ValidationResult[] }>((resolve, reject) => {
            data.Other = otherData;
            const UPDATED_DATA = cloneDeep(data);
            delete UPDATED_DATA.Header;
            this._cordMidOfficeService.validateAxData(UPDATED_DATA, applicationId)
                .then((response: void | ValidationErrors[]) => {
                    if (response) {
                        resolve(this.processValidationResults(data, response));
                    } else {
                        this.alerts$.next([]);
                        resolve({ result: false, data: undefined });
                    }
                })
                .catch((error) => {
                    this.alerts$.next([]);
                    if (error.response.status === 404) {
                        resolve({ result: false, data: undefined });
                    } else if (error.response.status === 400) {
                        resolve(this.processValidationResults(data, error.result));
                    } else {
                        reject(error);
                    }
                });
        });
    }

    private processValidationResults(data, errors: ValidationErrors[]): Promise<{ result: boolean, data: ValidationResult[] }> {
        return new Promise<{ result: boolean, data: ValidationResult[] }>((resolve, reject) => {
            let RESPONSE = false;
            if (errors && errors.length > 0) {
                errors.forEach(_r => {
                    if (_r.Message.indexOf('The Pattern constraint failed') > -1) {
                        _r.Message = 'De ingevulde data is niet correct';
                    }
                    if (_r.Message.indexOf('De aanvraag kan niet worden beoordeeld. De gegevens konden niet worden opgehaald. Om de aanvraag zelf te controleren kunt u naar de Hypobieb gaan. Deze vindt u rechtsboven aan op het Dashboard scherm.') > -1) {
                        RESPONSE = true;
                    }
                });

                this._variableService.validationErrors = errors;

                this.createErrorList(data);

                this.alerts$.next(this._variableService.validationErrors);
                resolve({ result: RESPONSE, data: errors });
            } else {
                this.alerts$.next([]);
                resolve({ result: false, data: undefined });
            }
        });
    }

    private createErrorList(model): any {
        const DATA = model;
        for (const prop in DATA) {
            if (prop) {
                let errorPath = [];
                if (DATA.hasOwnProperty(prop)) {

                    const value = DATA[prop];
                    if (typeof value === 'object') {
                        errorPath.push({ cord_ref: value['Cord_Ref'], propertyTitle: prop });
                        errorPath = this._helperService.searchChildItemForErrorList(value, errorPath);
                    } else {
                        if (prop.toLowerCase() === 'cord_ref') {
                            const ERROR_FOUND = this._variableService.validationErrors.find(x => x.Id === value);
                            if (ERROR_FOUND) {
                                errorPath.push({ cord_ref: value, propertyTitle: prop });
                            }
                        }
                    }
                }
            }
        }
        return DATA;
    }
}
